/**
 * WEB-2054 — кусок рендерера «БЛОКИ ПОРТАЛА управления подпиской».
 *
 * WEB-1152 (П9) / WEB-1395: список подписок, плановая отмена, возобновление и
 * оффер удержания. Данные и действия приходят из `PublicPortalProvider` (guid из
 * `?guid=` в `manage_link`); вне провайдера (канвас редактора) блоки рисуют
 * инертные превью.
 *
 * ПОЧЕМУ ОТДЕЛЬНЫМ КУСКОМ. Посетитель ВОРОНКИ этих блоков не видит никогда —
 * портал живёт на своей странице, по своей ссылке из письма. До разреза они
 * ехали ему вместе со всем рендерером: контекст действий портала, три кнопки и
 * карточка оффера. Ветки перенесены из `PublicBlockRendererExtended` без единой
 * правки разметки — менялось только то, ОТКУДА они приезжают.
 */
import React from 'react';
import type { EditorBlock } from '@/store/editorSlice';
import type { PublicExtendedBlockProps } from '@/player/publicExtendedBlockProps';
import { buttonSurfaceStyle } from '../blockBackgroundStyle';
import { resolveContentTextColor } from '../blockInnerSurface';
import {
  blockTextRoles,
  trimmedColor,
  textRoleStyle,
  type DownsellOfferAppearance,
} from '../semanticTextRoles';
import { SubscriptionListBlock } from '../portal/SubscriptionListBlock';
import { CancelSubscriptionButton } from '../portal/CancelSubscriptionButton';
import { ResumeSubscriptionButton } from '../portal/ResumeSubscriptionButton';
import { DownsellOfferBlock } from '../portal/DownsellOfferBlock';
import { withExtendedBlockShell } from '../extendedBlockShell';

/**
 * WEB-1692 юнит 3 — ВИД ПОРТАЛЬНОЙ КНОПКИ: заливка и цвет надписи, и НИЧЕГО
 * больше.
 *
 * Зовём общий сборщик кнопок (`buttonSurfaceStyle`, WEB-1651-2), но отдаём ему
 * РОВНО ТРИ поля своей карточки. Полный `buttonSurfaceStyle(block)` подмешал бы
 * ещё и рамку (`borderWidth`/`borderColor`/`borderRadius`) — а рамка кнопок это
 * WEB-1697, там свой список типов и свои контролы панели. Настройка, которая
 * действует, но контрола не имеет, — ровно тот дефект, который стережёт
 * `blockBorder.web1697.test.tsx`: он и покраснел на первой редакции этой
 * правки «рамка действует там, где настройки не показано».
 */
function portalButtonSurfaceStyle(block: EditorBlock): React.CSSProperties {
  return buttonSurfaceStyle({
    buttonBackground: block.buttonBackground,
    innerBackground: block.innerBackground,
    innerTextColor: block.innerTextColor,
  });
}

export function PortalBlocksRenderer({
  block,
  textColor,
  varCtx,
  stripePrices,
  followsExplicitSpacing,
}: PublicExtendedBlockProps): React.ReactElement | null {
  // WEB-1702 п.2: цвет текста ЭТОГО блока сильнее общего цвета воронки —
  // лестница слоёв в blockInnerSurface.tsx (resolveContentTextColor).
  const contentTextColor = resolveContentTextColor(block, textColor);
  const primaryColor = contentTextColor ?? 'var(--color-text-primary, inherit)';

  const rendered = ((): React.ReactElement | null => {
    switch (block.type) {
      // WEB-1152 (П9): блоки портала отмены с удержанием. Данные и действия —
      // из PublicPortalProvider (guid из ?guid= manage_link); вне провайдера
      // (канвас редактора) блоки рисуют инертные превью.
      case 'subscription_list': {
        // WEB-1692 юнит 3: заголовок и содержимое плашки — две роли; надпись
        // пустого состояния и заливка плашки — свои поля. Раньше сюда не
        // приходило НИ ОДНОГО оформительского значения.
        const listRoles = blockTextRoles(block, 'subscription_list');
        return (
          <SubscriptionListBlock
            title={block.subscriptionListTitle}
            emptyText={block.subscriptionListEmptyText}
            stripePrices={stripePrices}
            titleStyle={listRoles.title}
            rowStyle={listRoles.secondary}
            emptyStyle={textRoleStyle(
              block.subscriptionListEmptyColor,
              block.subscriptionListEmptyFontSize,
            )}
            cardBackgroundColor={block.subscriptionListCardBackgroundColor}
            // WEB-1905: язык ВОРОНКИ — портал это страница покупателя, его
            // надписи и дата продления идут из словаря плеера, а не из
            // английских констант и не по языку браузера.
            language={varCtx?.system?.locale}
          />
        );
      }

      case 'cancel_button':
        return (
          <CancelSubscriptionButton
            label={block.cancelButtonLabel}
            // WEB-1905: язык воронки (см. subscription_list выше).
            language={varCtx?.system?.locale}
            // WEB-1692 юнит 3 (решение владельца 25.08 — «опции точно такие
            // же, как у всех других кнопок… свободны»): заливка и цвет надписи
            // приходят тем же сборщиком, что у кнопок квиза и пейволла
            // (WEB-1651-2), кегль — общим полем кнопочной семьи. Пусто = {} и
            // прежние 14px, то есть сегодняшний вид до пикселя.
            surfaceStyle={portalButtonSurfaceStyle(block)}
            fontSize={block.fontSize}
          />
        );

      // WEB-1395: возобновление подписки (снятие запланированной отмены).
      case 'resume_button':
        return (
          <ResumeSubscriptionButton
            label={block.resumeButtonLabel}
            // WEB-1905: язык воронки (см. subscription_list выше).
            language={varCtx?.system?.locale}
            surfaceStyle={portalButtonSurfaceStyle(block)}
            fontSize={block.fontSize}
          />
        );

      case 'downsell_offer': {
        const offerRoles = blockTextRoles(block, 'downsell_offer');
        // WEB-1692 юнит 5: накладки собираются ОДИН раз здесь и едут в ОБЕ
        // двери карточки — сам блок и шаг подтверждения отмены
        // (CancelSubscriptionButton, WEB-1183), который берёт их из
        // конфигурации оффера в провайдере портала.
        const offerAppearance: DownsellOfferAppearance = {
          title: offerRoles.title,
          secondary: offerRoles.secondary,
          cta: textRoleStyle(
            block.downsellOfferCtaColor,
            block.downsellOfferCtaFontSize,
          ),
          ctaBackgroundColor: trimmedColor(
            block.downsellOfferCtaBackgroundColor,
          ),
          cardBackgroundColor: trimmedColor(
            block.downsellOfferCardBackgroundColor,
          ),
        };
        return (
          <DownsellOfferBlock
            // WEB-1905: язык воронки (см. subscription_list выше).
            language={varCtx?.system?.locale}
            title={block.downsellOfferTitle}
            text={block.downsellOfferText}
            ctaLabel={block.downsellOfferCtaLabel}
            successText={block.downsellOfferSuccessText}
            targetPriceId={block.downsellOfferTargetPriceId}
            sourcePriceIds={block.downsellOfferSourcePriceIds}
            stripePrices={stripePrices}
            primaryColor={primaryColor}
            // WEB-1692 юнит 5 (репорт владельца 24.08, пять требований одной
            // фразой). Накладки считает ОДНА функция и на обе двери карточки:
            // блок отдаёт их дальше в конфигурацию оффера, откуда их берёт шаг
            // подтверждения отмены (CancelSubscriptionButton, WEB-1183).
            appearance={offerAppearance}
          />
        );
      }

      default:
        return null;
    }
  })();

  return withExtendedBlockShell(block, rendered, followsExplicitSpacing);
}
